import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Me } from "@workcore/schemas";
import { api, checkedWrite, get } from "../api";
import { useDefinitions, useRefresh } from "../directory";
import { EmptyState, ErrorBox, Field, PageHeader, Panel, SidePanel, StatusBadge, When, when } from "../ui";
import { toKey } from "./editor/shared";

export function WorkflowsPage() {
  const { slug = "" } = useParams();
  const defs = useDefinitions(slug);
  const refresh = useRefresh(slug);
  const me = useQuery({ queryKey: ["ws-me", slug], queryFn: async () => Me.parse(await get(`/v1/ws/${slug}/me`)) });
  const admin = me.data?.isSystemAdmin ?? false;
  const mine = new Set((me.data?.roles ?? []).filter((r) => r.role === "workflow_admin").map((r) => r.scopeId));
  const [name, setName] = useState("");
  const file = useRef<HTMLInputElement>(null);
  const [adding, setAdding] = useState(false);
  const create = useMutation({ mutationFn: () => checkedWrite("POST", `/v1/ws/${slug}/definitions`, { key: toKey(name), name }), onSuccess: () => (setName(""), setAdding(false), refresh()) });
  const importer = useMutation({
    mutationFn: async (text: string) => checkedWrite("POST", `/v1/ws/${slug}/definitions/import`, { definition: JSON.parse(text) as unknown }),
    onSuccess: refresh,
  });
  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void f.text().then((t) => importer.mutate(t));
    e.target.value = "";
  };
  return (
    <>
      <PageHeader
        title="Workflows"
        description="A workflow is the vocabulary projects use: object types, what can contain what, states and fields. Edits apply at once to every project using it."
        actions={
          admin && (
            <>
              <button type="button" className="secondary" onClick={() => file.current?.click()}>Import JSON…</button>
              <button onClick={() => setAdding(true)}>New workflow</button>
            </>
          )
        }
      />
      <input ref={file} type="file" accept="application/json,.json" hidden onChange={onFile} />
      <ErrorBox error={importer.error} />
      <SidePanel title="New workflow" open={adding} onClose={() => setAdding(false)} onSubmit={() => create.mutate()} submitLabel="Create workflow" busy={create.isPending} error={create.error}>
        <Field label="Name" help={`Key: ${toKey(name) || "…"} (it never changes)`}>
          <input required value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="Consultancy engagement" />
        </Field>
      </SidePanel>
      <ErrorBox error={defs.error} />
      <table className="grid">
        <thead><tr><th>Name</th><th>Key</th><th>Status</th><th>Updated</th><th>You</th></tr></thead>
        <tbody>
          {defs.data?.map((d) => (
            <tr key={d.id}>
              <td><Link to={`/w/${slug}/workflows/${d.id}`}>{d.name}</Link></td>
              <td><code>{d.key}</code></td>
              <td><StatusBadge status={d.status} /></td>
              <td><When at={d.updatedAt} /></td>
              <td className="muted small">{admin ? "system admin" : mine.has(d.id) ? "workflow admin" : "read only"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {defs.data?.length === 0 && <EmptyState title="No workflows yet" body="A workflow defines the types of work, how they nest, their states and fields. Create one, or import a JSON export from another workspace." action={admin && <button onClick={() => setAdding(true)}>New workflow</button>} />}
    </>
  );
}

export async function downloadExport(slug: string, id: number, key: string): Promise<void> {
  const data = await api("POST", `/v1/ws/${slug}/definitions/${id}/export`);
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${key}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}
